// SIAE Borderò (Mod. 108) — Admin section
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FileText, Plus, Trash2, Edit3, Search, X, Download, Save,
  ChevronUp, ChevronDown, Settings2, Music2, Loader2, Calendar,
  Star, Library, TrendingUp,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_TYPES = [
  { value: 'musica_dal_vivo', label: 'Musica dal vivo' },
  { value: 'dj_set', label: 'DJ set' },
  { value: 'karaoke', label: 'Karaoke' },
];
const VENUES = [
  { value: 'peschiera', label: 'La Peschiera Club' },
  { value: 'jimmys', label: "Jimmy's Club" },
  { value: 'other', label: 'Altro / Esterno' },
];

const secToMMSS = (s) => {
  const n = Math.max(0, parseInt(s || 0));
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};
const mmssToSec = (str) => {
  if (!str) return 0;
  const parts = String(str).split(':');
  if (parts.length === 2) return parseInt(parts[0] || 0) * 60 + parseInt(parts[1] || 0);
  return parseInt(str) || 0;
};

// ─────────────────────────────────────────────────────────────
// PDF generator — Mod. 108 SIAE (fac-simile)
// ─────────────────────────────────────────────────────────────
function generateSiaePdf(session, settings) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 10;

  // ── Header
  doc.setFillColor(240, 240, 240);
  doc.rect(marginX, 10, pageW - marginX * 2, 18, 'F');
  doc.setDrawColor(0);
  doc.rect(marginX, 10, pageW - marginX * 2, 18);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('SIAE', marginX + 4, 18);
  doc.setFontSize(11);
  doc.text('PROGRAMMA MUSICALE — MOD. 108', pageW / 2, 18, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Fac-simile', pageW - marginX - 4, 26, { align: 'right' });
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.text('Documento non ufficiale — copia di lavoro', pageW / 2, 26, { align: 'center' });

  // ── Locale info
  let y = 34;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('DATI LOCALE / ORGANIZZATORE', marginX, y); y += 4;
  doc.setDrawColor(120);
  doc.line(marginX, y, pageW - marginX, y); y += 4;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);

  const leftCol = marginX + 2;
  const rightCol = pageW / 2 + 5;
  const row = (labelL, valueL, labelR, valueR) => {
    doc.setFont('helvetica', 'bold');
    doc.text(labelL, leftCol, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(valueL || '________________________'), leftCol + 38, y);
    if (labelR) {
      doc.setFont('helvetica', 'bold');
      doc.text(labelR, rightCol, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(valueR || '________________________'), rightCol + 34, y);
    }
    y += 5;
  };
  row('Ragione sociale:', settings.ragione_sociale, 'Cod. SIAE:', settings.codice_siae);
  row('Indirizzo:', settings.indirizzo, 'P.IVA:', settings.partita_iva);
  row('Città:', settings.citta, 'N. licenza:', settings.licenza_number);

  // ── Manifestazione info
  y += 3;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('DATI MANIFESTAZIONE', marginX, y); y += 4;
  doc.line(marginX, y, pageW - marginX, y); y += 4;
  doc.setFont('helvetica', 'normal');
  const venueLabel = VENUES.find(v => v.value === session.venue_type)?.label || session.venue_type;
  const typeLabel = SESSION_TYPES.find(t => t.value === session.session_type)?.label || session.session_type;
  const dateStr = session.performance_date
    ? new Date(session.performance_date + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
  const orario = [session.time_from, session.time_to].filter(Boolean).join(' — ') || '________________';
  row('Data:', dateStr, 'Orario:', orario);
  row('Sala:', venueLabel, 'Tipo esecuzione:', typeLabel);
  row('Titolo manifestazione:', session.title || session.event_title || '', 'Esecutore/Orchestra:', session.performer_name);

  // ── Table brani
  y += 3;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text('PROGRAMMA MUSICALE ESEGUITO', marginX, y);
  y += 3;

  const rows = (session.songs || []).map((s, idx) => [
    String(idx + 1),
    s.title || '',
    s.author || '',
    s.composer || '',
    s.performer || '',
    s.publisher || '',
    secToMMSS(s.duration_seconds),
  ]);
  autoTable(doc, {
    startY: y,
    head: [['N.', 'Titolo brano', 'Autore (testo)', 'Compositore (musica)', 'Interprete', 'Editore', 'Durata']],
    body: rows.length ? rows : [['1', '', '', '', '', '', '']],
    styles: {
      fontSize: 8,
      cellPadding: 1.6,
      lineColor: [80, 80, 80],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [230, 230, 230],
      textColor: 20,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 45 },
      2: { cellWidth: 35 },
      3: { cellWidth: 35 },
      4: { cellWidth: 30 },
      5: { cellWidth: 25 },
      6: { cellWidth: 12, halign: 'center' },
    },
    margin: { left: marginX, right: marginX },
  });

  // ── Totals + Signature
  const finalY = doc.lastAutoTable.finalY + 5;
  const totalSec = (session.songs || []).reduce((s, x) => s + (parseInt(x.duration_seconds) || 0), 0);
  const totalStr = secToMMSS(totalSec);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text(`Totale brani: ${(session.songs || []).length}`, marginX, finalY);
  doc.text(`Durata totale: ${totalStr}`, marginX + 60, finalY);

  const sigY = Math.min(finalY + 22, 275);
  doc.setDrawColor(120);
  doc.line(marginX + 8, sigY, marginX + 80, sigY);
  doc.line(pageW - marginX - 80, sigY, pageW - marginX - 8, sigY);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.text("Data e timbro del locale", marginX + 8, sigY + 4);
  doc.text("Firma del Direttore Artistico", pageW - marginX - 80, sigY + 4);

  // Footer
  doc.setFontSize(7); doc.setTextColor(120);
  doc.text('Documento generato automaticamente — verificare prima dell\'invio SIAE', pageW / 2, 290, { align: 'center' });

  const filename = `bordero_${(session.performance_date || 'senza-data')}_${(session.title || session.event_title || 'programma').replace(/[^a-z0-9]+/gi, '_')}.pdf`;
  doc.save(filename);
}

// ─────────────────────────────────────────────────────────────
// Song search dropdown
// ─────────────────────────────────────────────────────────────
function SongSearchInput({ token, onPick }) {
  const [q, setQ] = useState('');
  const [libResults, setLibResults] = useState([]);
  const [mbResults, setMbResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const doSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) { setLibResults([]); setMbResults([]); return; }
    setLoading(true);
    try {
      const [lib, mb] = await Promise.all([
        axios.get(`${API}/admin/siae/library`, { params: { q: query }, headers: { Authorization: `Bearer ${token}` } }).then(r => r.data || []).catch(() => []),
        axios.get(`${API}/admin/siae/search-song`, { params: { q: query }, headers: { Authorization: `Bearer ${token}` } }).then(r => r.data.results || []).catch(() => []),
      ]);
      setLibResults(lib.slice(0, 6));
      setMbResults(mb);
      setOpen(true);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setLibResults([]); setMbResults([]); setOpen(false); return; }
    timer.current = setTimeout(() => doSearch(q), 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, doSearch]);

  const totalResults = libResults.length + mbResults.length;

  return (
    <div className="relative" data-testid="siae-song-search">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder='Cerca canzone… (prima in libreria, poi online)'
          className="w-full pl-9 pr-9 py-2.5 rounded-xl text-white outline-none transition-all"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(201,168,76,0.25)',
          }}
          data-testid="siae-search-input"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gold animate-spin" />}
      </div>
      {open && totalResults > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 rounded-xl overflow-hidden max-h-96 overflow-y-auto"
          data-testid="siae-search-dropdown"
          style={{ background: '#0e0e14', border: '1px solid rgba(201,168,76,0.4)', boxShadow: '0 15px 40px rgba(0,0,0,0.5)' }}
        >
          {libResults.length > 0 && (
            <>
              <div className="px-3 py-1.5 font-manrope font-700 uppercase tracking-wider text-[10px] text-gold flex items-center gap-1.5" style={{ background: 'rgba(201,168,76,0.08)' }}>
                <Star size={11} className="fill-current" /> Libreria brani
              </div>
              {libResults.map((r, i) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => { onPick({ ...r, __from_library: true, library_id: r.id }); setQ(''); setLibResults([]); setMbResults([]); setOpen(false); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors border-b border-white/5"
                  data-testid={`siae-library-result-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-manrope font-700 text-white text-sm truncate">{r.title}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.times_used > 0 && <span className="text-gray-500 font-manrope text-[10px] flex items-center gap-0.5"><TrendingUp size={10} />×{r.times_used}</span>}
                      <span className="text-gold font-manrope text-xs">{secToMMSS(r.duration_seconds)}</span>
                    </div>
                  </div>
                  <p className="text-gray-400 font-manrope text-xs truncate">
                    {r.performer || '—'}{r.composer ? ` · Comp: ${r.composer}` : ''}
                  </p>
                </button>
              ))}
            </>
          )}
          {mbResults.length > 0 && (
            <>
              <div className="px-3 py-1.5 font-manrope font-700 uppercase tracking-wider text-[10px] text-blue-300 flex items-center gap-1.5" style={{ background: 'rgba(96,165,250,0.08)' }}>
                <Search size={11} /> Risultati online (MusicBrainz)
              </div>
              {mbResults.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { onPick(r); setQ(''); setLibResults([]); setMbResults([]); setOpen(false); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                  data-testid={`siae-search-result-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-manrope font-700 text-white text-sm truncate">{r.title}</p>
                    <span className="text-gold font-manrope text-xs shrink-0">{secToMMSS(r.duration_seconds)}</span>
                  </div>
                  <p className="text-gray-400 font-manrope text-xs truncate">
                    {r.performer || '—'}{r.composer ? ` · Comp: ${r.composer}` : ''}
                  </p>
                </button>
              ))}
            </>
          )}
        </div>
      )}
      {open && !loading && q.trim().length >= 2 && totalResults === 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-xl px-4 py-3 text-sm text-gray-400 font-manrope" style={{ background: '#0e0e14', border: '1px solid rgba(255,255,255,0.1)' }}>
          Nessun risultato. Aggiungi manualmente qui sotto.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Session editor modal
// ─────────────────────────────────────────────────────────────
function SessionEditorModal({ token, session, settings, allEvents, onClose, onSaved }) {
  const isEdit = !!session?.id;
  const [form, setForm] = useState({
    title: session?.title || '',
    event_id: session?.event_id || '',
    event_title: session?.event_title || '',
    venue_type: session?.venue_type || 'peschiera',
    performance_date: session?.performance_date || new Date().toISOString().slice(0, 10),
    time_from: session?.time_from || '21:00',
    time_to: session?.time_to || '02:00',
    performer_name: session?.performer_name || '',
    session_type: session?.session_type || 'musica_dal_vivo',
    songs: session?.songs || [],
    notes: session?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const totalDuration = form.songs.reduce((s, x) => s + (parseInt(x.duration_seconds) || 0), 0);

  const setSong = (idx, patch) => {
    setForm(f => {
      const songs = [...f.songs];
      songs[idx] = { ...songs[idx], ...patch };
      return { ...f, songs };
    });
  };
  const removeSong = (idx) => setForm(f => ({ ...f, songs: f.songs.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })) }));
  const moveSong = (idx, dir) => {
    setForm(f => {
      const songs = [...f.songs];
      const to = idx + dir;
      if (to < 0 || to >= songs.length) return f;
      [songs[idx], songs[to]] = [songs[to], songs[idx]];
      return { ...f, songs: songs.map((s, i) => ({ ...s, order: i + 1 })) };
    });
  };
  const addSong = (payload = {}) => {
    setForm(f => ({
      ...f,
      songs: [
        ...f.songs,
        {
          order: f.songs.length + 1,
          title: payload.title || '',
          author: payload.author || '',
          composer: payload.composer || '',
          performer: payload.performer || '',
          publisher: payload.publisher || '',
          duration_seconds: payload.duration_seconds || 0,
          _library_id: payload.library_id || null,
        },
      ],
    }));
  };

  const saveToLibrary = async (idx) => {
    const s = form.songs[idx];
    if (!s || !s.title) {
      toast.error('Titolo obbligatorio per salvare in libreria.');
      return;
    }
    if (s._library_id) {
      toast.info('Questo brano è già nella libreria.');
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/siae/library`, {
        title: s.title, author: s.author, composer: s.composer,
        performer: s.performer, publisher: s.publisher,
        duration_seconds: parseInt(s.duration_seconds) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSong(idx, { _library_id: r.data.id });
      toast.success('★ Salvato in libreria.');
    } catch {
      toast.error('Errore nel salvataggio libreria.');
    }
  };

  const handleEventChange = (event_id) => {
    if (!event_id) {
      setForm(f => ({ ...f, event_id: '', event_title: '' }));
      return;
    }
    const ev = allEvents.find(e => e.id === event_id);
    if (ev) {
      setForm(f => ({
        ...f,
        event_id,
        event_title: ev.title,
        performance_date: ev.date || f.performance_date,
        time_from: ev.time || f.time_from,
        venue_type: ev.club === 'jimmys' ? 'jimmys' : 'peschiera',
        performer_name: f.performer_name || ev.dj || '',
      }));
    }
  };

  const save = async () => {
    // Client-side validation
    if (!form.performance_date) {
      setError('Inserisci la data della serata.');
      return;
    }
    setSaving(true); setError('');
    try {
      // Strip internal-only fields (_library_id) before sending to backend
      const cleanSongs = form.songs.map(({ _library_id, ...rest }) => rest);
      const payload = { ...form, songs: cleanSongs };
      if (isEdit) {
        await axios.put(`${API}/admin/siae/sessions/${session.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/siae/sessions`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      // Increment library usage counter for songs pulled from library
      const libIds = form.songs.map(s => s._library_id).filter(Boolean);
      await Promise.all(libIds.map(id =>
        axios.post(`${API}/admin/siae/library/${id}/use`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
      ));
      toast.success('Borderò salvato.');
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || 'Errore campo').join(', ') : 'Errore durante il salvataggio.');
      setError(msg);
    } finally { setSaving(false); }
  };

  const exportPdf = () => {
    generateSiaePdf(form, settings || {});
  };

  return (
    <div className="fixed inset-0 z-50 p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="min-h-full flex items-start justify-center py-8">
      <div
        className="w-full max-w-5xl rounded-3xl p-6 md:p-8"
        style={{ background: 'linear-gradient(180deg, #0f0f16, #0a0a10)', border: '1px solid rgba(201,168,76,0.25)' }}
        data-testid="siae-editor-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-2xl font-bold text-white flex items-center gap-3">
            <FileText size={22} className="text-gold" />
            {isEdit ? 'Modifica borderò' : 'Nuovo borderò SIAE'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="siae-close-editor"><X size={22} /></button>
        </div>

        {/* Header session */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Collega a evento (opzionale)</label>
            <select
              value={form.event_id}
              onChange={e => handleEventChange(e.target.value)}
              className="input-elegant"
              data-testid="siae-event-select"
            >
              <option value="">— Indipendente —</option>
              {allEvents.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.date} · {ev.title} {ev.club === 'jimmys' ? "(Jimmy's)" : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Titolo manifestazione</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder={form.event_title || 'Es. Serata Liscio'}
              className="input-elegant" data-testid="siae-title-input" />
          </div>
          <div>
            <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Data serata *</label>
            <input type="date" value={form.performance_date} onChange={e => setForm(f => ({ ...f, performance_date: e.target.value }))}
              className="input-elegant" data-testid="siae-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Dalle</label>
              <input type="time" value={form.time_from} onChange={e => setForm(f => ({ ...f, time_from: e.target.value }))}
                className="input-elegant" />
            </div>
            <div>
              <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Alle</label>
              <input type="time" value={form.time_to} onChange={e => setForm(f => ({ ...f, time_to: e.target.value }))}
                className="input-elegant" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Sala</label>
            <select value={form.venue_type} onChange={e => setForm(f => ({ ...f, venue_type: e.target.value }))}
              className="input-elegant" data-testid="siae-venue-select">
              {VENUES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Tipo esecuzione</label>
            <select value={form.session_type} onChange={e => setForm(f => ({ ...f, session_type: e.target.value }))}
              className="input-elegant" data-testid="siae-type-select">
              {SESSION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Esecutore / Orchestra / DJ</label>
            <input type="text" value={form.performer_name} onChange={e => setForm(f => ({ ...f, performer_name: e.target.value }))}
              placeholder="Es. Orchestra Casadei" className="input-elegant" data-testid="siae-performer-input" />
          </div>
        </div>

        {/* Song search */}
        <div className="mb-4 p-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <p className="font-manrope font-700 text-white text-sm mb-2 flex items-center gap-2">
            <Music2 size={16} className="text-gold" />
            Aggiungi brano
          </p>
          <SongSearchInput token={token} onPick={(r) => addSong(r)} />
          <button
            type="button"
            onClick={() => addSong({})}
            data-testid="siae-add-manual-btn"
            className="mt-3 flex items-center gap-2 text-gold hover:text-white font-manrope font-600 text-sm transition-colors"
          >
            <Plus size={14} /> Aggiungi manualmente (riga vuota)
          </button>
        </div>

        {/* Songs table */}
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '900px' }} data-testid="siae-songs-table">
              <thead style={{ background: 'rgba(201,168,76,0.15)' }}>
                <tr className="text-gold font-manrope font-700 uppercase tracking-wider text-[11px]">
                  <th className="px-2 py-2 text-center" style={{ width: '32px' }}>#</th>
                  <th className="px-2 py-2 text-left" style={{ minWidth: '180px' }}>Titolo</th>
                  <th className="px-2 py-2 text-left" style={{ minWidth: '140px' }}>Autore (testo)</th>
                  <th className="px-2 py-2 text-left" style={{ minWidth: '150px' }}>Compositore (musica)</th>
                  <th className="px-2 py-2 text-left" style={{ minWidth: '130px' }}>Interprete</th>
                  <th className="px-2 py-2 text-left" style={{ minWidth: '110px' }}>Editore</th>
                  <th className="px-2 py-2 text-center" style={{ width: '80px' }}>Durata</th>
                  <th className="px-2 py-2" style={{ width: '128px' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.songs.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500 font-manrope text-sm">
                    Nessun brano ancora. Cerca o aggiungi manualmente.
                  </td></tr>
                ) : form.songs.map((s, idx) => (
                  <tr key={idx} className="border-t border-white/5" data-testid={`siae-song-row-${idx}`}>
                    <td className="px-2 py-1.5 text-center text-gold font-manrope font-700 text-xs">{idx + 1}</td>
                    <td className="px-1 py-1"><input value={s.title || ''} onChange={e => setSong(idx, { title: e.target.value })} className="w-full px-2 py-1 rounded bg-black/40 text-white text-xs outline-none border border-transparent focus:border-gold" data-testid={`siae-song-title-${idx}`} /></td>
                    <td className="px-1 py-1"><input value={s.author || ''} onChange={e => setSong(idx, { author: e.target.value })} className="w-full px-2 py-1 rounded bg-black/40 text-white text-xs outline-none border border-transparent focus:border-gold" /></td>
                    <td className="px-1 py-1"><input value={s.composer || ''} onChange={e => setSong(idx, { composer: e.target.value })} className="w-full px-2 py-1 rounded bg-black/40 text-white text-xs outline-none border border-transparent focus:border-gold" /></td>
                    <td className="px-1 py-1"><input value={s.performer || ''} onChange={e => setSong(idx, { performer: e.target.value })} className="w-full px-2 py-1 rounded bg-black/40 text-white text-xs outline-none border border-transparent focus:border-gold" /></td>
                    <td className="px-1 py-1"><input value={s.publisher || ''} onChange={e => setSong(idx, { publisher: e.target.value })} className="w-full px-2 py-1 rounded bg-black/40 text-white text-xs outline-none border border-transparent focus:border-gold" /></td>
                    <td className="px-1 py-1"><input value={secToMMSS(s.duration_seconds)} onChange={e => setSong(idx, { duration_seconds: mmssToSec(e.target.value) })} placeholder="mm:ss" className="w-full px-2 py-1 rounded bg-black/40 text-white text-xs text-center outline-none border border-transparent focus:border-gold" /></td>
                    <td className="px-1 py-1">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => saveToLibrary(idx)}
                          data-testid={`siae-song-save-lib-${idx}`}
                          className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${s._library_id ? 'text-gold' : 'text-gray-400 hover:text-gold'}`}
                          title={s._library_id ? 'Già in libreria' : 'Salva in libreria'}
                        >
                          <Star size={13} className={s._library_id ? 'fill-current' : ''} />
                        </button>
                        <button type="button" onClick={() => moveSong(idx, -1)} disabled={idx === 0} className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-30" title="Su">
                          <ChevronUp size={14} />
                        </button>
                        <button type="button" onClick={() => moveSong(idx, 1)} disabled={idx === form.songs.length - 1} className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-30" title="Giù">
                          <ChevronDown size={14} />
                        </button>
                        <button type="button" onClick={() => removeSong(idx)} data-testid={`siae-song-remove-${idx}`} className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-red-400" title="Rimuovi">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {form.songs.length > 0 && (
                <tfoot style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <tr>
                    <td colSpan={6} className="px-3 py-2 text-right font-manrope font-700 text-white text-xs">
                      Totale brani: {form.songs.length}
                    </td>
                    <td colSpan={2} className="px-3 py-2 text-left font-manrope font-700 text-gold text-xs">
                      Durata totale: {secToMMSS(totalDuration)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {error && <p className="text-red-400 font-manrope text-sm mt-4">{error}</p>}

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center" data-testid="siae-cancel-btn">
            Chiudi
          </button>
          <button
            onClick={exportPdf}
            disabled={form.songs.length === 0}
            data-testid="siae-export-pdf-btn"
            className="btn-secondary flex-1 justify-center"
            style={{ opacity: form.songs.length === 0 ? 0.5 : 1 }}
          >
            <Download size={16} /> Esporta PDF Mod. 108
          </button>
          <button onClick={save} disabled={saving} className="btn-primary flex-1 justify-center" data-testid="siae-save-btn">
            {saving ? 'Salvataggio…' : <><Save size={16} /> Salva borderò</>}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Settings modal
// ─────────────────────────────────────────────────────────────
function SiaeSettingsPanel({ token, settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => setForm(settings || {}), [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/siae/settings`, form, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      toast.success('Impostazioni salvate.');
      setTimeout(() => setSaved(false), 2000);
      onSaved(form);
    } catch (e) {
      toast.error('Errore nel salvataggio impostazioni.');
    } finally { setSaving(false); }
  };

  return (
    <div className="card-dark p-6 mb-8" data-testid="siae-settings-panel">
      <h3 className="font-playfair text-lg font-bold text-white mb-1 flex items-center gap-2">
        <Settings2 size={17} className="text-gold" />
        Dati locale (intestazione borderò)
      </h3>
      <p className="text-gray-400 font-manrope text-sm mb-5">Questi dati verranno stampati in alto in ogni PDF SIAE.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Ragione sociale</label>
          <input value={form.ragione_sociale || ''} onChange={e => setForm(f => ({ ...f, ragione_sociale: e.target.value }))} className="input-elegant" placeholder="Es. La Peschiera Snc" data-testid="siae-set-rag-sociale" />
        </div>
        <div>
          <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Codice SIAE</label>
          <input value={form.codice_siae || ''} onChange={e => setForm(f => ({ ...f, codice_siae: e.target.value }))} className="input-elegant" placeholder="Es. 12345" data-testid="siae-set-codice" />
        </div>
        <div>
          <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Indirizzo</label>
          <input value={form.indirizzo || ''} onChange={e => setForm(f => ({ ...f, indirizzo: e.target.value }))} className="input-elegant" placeholder="Es. Via Monte Rosa 10" />
        </div>
        <div>
          <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">Città</label>
          <input value={form.citta || ''} onChange={e => setForm(f => ({ ...f, citta: e.target.value }))} className="input-elegant" placeholder="Valdengo (BI)" />
        </div>
        <div>
          <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">P.IVA</label>
          <input value={form.partita_iva || ''} onChange={e => setForm(f => ({ ...f, partita_iva: e.target.value }))} className="input-elegant" placeholder="Es. IT01234567890" data-testid="siae-set-piva" />
        </div>
        <div>
          <label className="block text-xs font-manrope font-700 uppercase tracking-wider text-gold mb-1">N. licenza (opz.)</label>
          <input value={form.licenza_number || ''} onChange={e => setForm(f => ({ ...f, licenza_number: e.target.value }))} className="input-elegant" />
        </div>
      </div>
      <button onClick={save} disabled={saving} className="btn-primary mt-5" data-testid="siae-save-settings">
        {saving ? 'Salvataggio…' : saved ? '✓ Salvato' : <><Save size={15} /> Salva impostazioni</>}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Library panel (CRUD brani salvati)
// ─────────────────────────────────────────────────────────────
function LibraryPanel({ token }) {
  const [songs, setSongs] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // song obj or {} for new
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/siae/library`, { params: q ? { q } : {}, headers });
      setSongs(r.data || []);
    } catch { toast.error('Errore caricamento libreria.'); }
    finally { setLoading(false); }
  }, [q, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || '',
        author: editing.author || '',
        composer: editing.composer || '',
        performer: editing.performer || '',
        publisher: editing.publisher || '',
        duration_seconds: editing.duration_seconds || 0,
      });
    } else { setForm(null); }
  }, [editing]);

  const save = async () => {
    if (!form?.title?.trim()) { toast.error('Titolo obbligatorio.'); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/siae/library/${editing.id}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/siae/library`, form, { headers });
      }
      toast.success('Brano salvato in libreria.');
      setEditing(null);
      load();
    } catch { toast.error('Errore salvataggio.'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Rimuovere questo brano dalla libreria?')) return;
    await axios.delete(`${API}/admin/siae/library/${id}`, { headers });
    load();
  };

  return (
    <div data-testid="siae-library-panel">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Cerca in libreria (titolo, autore, compositore, interprete)…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-white outline-none transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}
            data-testid="siae-library-search"
          />
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="siae-library-new-btn"
          className="btn-primary"
        >
          <Plus size={16} /> Aggiungi brano
        </button>
      </div>

      {loading ? (
        <div className="card-dark p-10 text-center text-gray-400 font-manrope">Caricamento…</div>
      ) : songs.length === 0 ? (
        <div className="card-dark p-10 text-center" data-testid="siae-library-empty">
          <Library size={40} className="mx-auto text-gold opacity-40 mb-3" />
          <p className="text-white font-playfair text-lg mb-1">Libreria vuota</p>
          <p className="text-gray-400 font-manrope text-sm">Aggiungi qui i brani che suoni più spesso: li richiami in un click nei nuovi borderò.</p>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '900px' }}>
              <thead style={{ background: 'rgba(201,168,76,0.15)' }}>
                <tr className="text-gold font-manrope font-700 uppercase tracking-wider text-[11px]">
                  <th className="px-2 py-2 text-left">Titolo</th>
                  <th className="px-2 py-2 text-left">Autore (testo)</th>
                  <th className="px-2 py-2 text-left">Compositore (musica)</th>
                  <th className="px-2 py-2 text-left">Interprete</th>
                  <th className="px-2 py-2 text-left">Editore</th>
                  <th className="px-2 py-2 text-center" style={{ width: '80px' }}>Durata</th>
                  <th className="px-2 py-2 text-center" style={{ width: '70px' }}>Uso</th>
                  <th className="px-2 py-2" style={{ width: '90px' }}></th>
                </tr>
              </thead>
              <tbody>
                {songs.map(s => (
                  <tr key={s.id} className="border-t border-white/5" data-testid={`siae-library-row-${s.id}`}>
                    <td className="px-2 py-2 text-white font-manrope font-600 text-sm">{s.title}</td>
                    <td className="px-2 py-2 text-gray-300 font-manrope text-xs">{s.author || '—'}</td>
                    <td className="px-2 py-2 text-gray-300 font-manrope text-xs">{s.composer || '—'}</td>
                    <td className="px-2 py-2 text-gray-300 font-manrope text-xs">{s.performer || '—'}</td>
                    <td className="px-2 py-2 text-gray-300 font-manrope text-xs">{s.publisher || '—'}</td>
                    <td className="px-2 py-2 text-center text-gold font-manrope text-xs">{secToMMSS(s.duration_seconds)}</td>
                    <td className="px-2 py-2 text-center font-manrope text-xs">
                      {s.times_used > 0 ? <span className="text-gold font-700">×{s.times_used}</span> : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setEditing(s)} data-testid={`siae-library-edit-${s.id}`} className="w-8 h-8 rounded flex items-center justify-center text-gray-400 hover:text-gold" title="Modifica"><Edit3 size={13} /></button>
                        <button onClick={() => remove(s.id)} data-testid={`siae-library-delete-${s.id}`} className="w-8 h-8 rounded flex items-center justify-center text-gray-400 hover:text-red-400" title="Elimina"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }} data-testid="siae-library-edit-modal">
          <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: 'linear-gradient(180deg, #0f0f16, #0a0a10)', border: '1px solid rgba(201,168,76,0.3)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-playfair text-xl font-bold text-white flex items-center gap-2">
                <Library size={18} className="text-gold" />
                {editing?.id ? 'Modifica brano' : 'Nuovo brano in libreria'}
              </h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-manrope font-700 uppercase text-gold mb-1">Titolo *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="input-elegant" data-testid="lib-form-title" />
              </div>
              <div>
                <label className="block text-xs font-manrope font-700 uppercase text-gold mb-1">Autore (testo)</label>
                <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="input-elegant" />
              </div>
              <div>
                <label className="block text-xs font-manrope font-700 uppercase text-gold mb-1">Compositore (musica)</label>
                <input value={form.composer} onChange={e => setForm(f => ({ ...f, composer: e.target.value }))} className="input-elegant" />
              </div>
              <div>
                <label className="block text-xs font-manrope font-700 uppercase text-gold mb-1">Interprete</label>
                <input value={form.performer} onChange={e => setForm(f => ({ ...f, performer: e.target.value }))} className="input-elegant" />
              </div>
              <div>
                <label className="block text-xs font-manrope font-700 uppercase text-gold mb-1">Editore</label>
                <input value={form.publisher} onChange={e => setForm(f => ({ ...f, publisher: e.target.value }))} className="input-elegant" />
              </div>
              <div>
                <label className="block text-xs font-manrope font-700 uppercase text-gold mb-1">Durata (mm:ss)</label>
                <input value={secToMMSS(form.duration_seconds)} onChange={e => setForm(f => ({ ...f, duration_seconds: mmssToSec(e.target.value) }))} placeholder="03:30" className="input-elegant text-center" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="btn-secondary flex-1 justify-center">Annulla</button>
              <button onClick={save} disabled={saving} className="btn-primary flex-1 justify-center" data-testid="siae-library-save-btn">
                {saving ? 'Salvataggio…' : <><Save size={15} /> Salva</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main section
// ─────────────────────────────────────────────────────────────
export default function SiaeSection({ token }) {
  const [view, setView] = useState('bordero'); // 'bordero' | 'library'
  const [sessions, setSessions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | session (edit)
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ss, st, ev] = await Promise.all([
        axios.get(`${API}/admin/siae/sessions`, { headers }),
        axios.get(`${API}/admin/siae/settings`, { headers }),
        axios.get(`${API}/events`, { params: { club: 'all' } }),
      ]);
      setSessions(ss.data);
      setSettings(st.data);
      setAllEvents(ev.data);
    } catch (e) {
      toast.error('Errore nel caricamento borderò.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Eliminare questo borderò?')) return;
    await axios.delete(`${API}/admin/siae/sessions/${id}`, { headers });
    load();
  };

  const exportOne = (s) => generateSiaePdf(s, settings || {});

  return (
    <div data-testid="siae-section">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <FileText size={26} className="text-gold" />
          Borderò SIAE (Mod. 108)
        </h1>
        {view === 'bordero' && (
          <button
            onClick={() => setEditing({})}
            data-testid="siae-new-btn"
            className="btn-primary"
          >
            <Plus size={16} /> Nuovo borderò
          </button>
        )}
      </div>

      {/* Sub-nav */}
      <div className="flex items-center gap-1 sm:gap-2 p-1.5 rounded-2xl w-fit mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="siae-subnav">
        <button
          onClick={() => setView('bordero')}
          data-testid="siae-subnav-bordero"
          className={`px-4 sm:px-5 py-2 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center gap-2 ${view === 'bordero' ? 'text-white' : 'text-gray-400 hover:text-gray-200'}`}
          style={view === 'bordero' ? {
            background: 'linear-gradient(135deg, rgba(201,168,76,0.25), rgba(201,168,76,0.15))',
            border: '1px solid rgba(201,168,76,0.5)',
          } : {}}
        >
          <FileText size={14} /> Borderò
        </button>
        <button
          onClick={() => setView('library')}
          data-testid="siae-subnav-library"
          className={`px-4 sm:px-5 py-2 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center gap-2 ${view === 'library' ? 'text-white' : 'text-gray-400 hover:text-gray-200'}`}
          style={view === 'library' ? {
            background: 'linear-gradient(135deg, rgba(201,168,76,0.25), rgba(201,168,76,0.15))',
            border: '1px solid rgba(201,168,76,0.5)',
          } : {}}
        >
          <Library size={14} /> Libreria brani
        </button>
      </div>

      {view === 'library' ? (
        <LibraryPanel token={token} />
      ) : (
        <>
      <SiaeSettingsPanel token={token} settings={settings} onSaved={s => setSettings(s)} />

      {/* Sessions list */}
      <h3 className="font-playfair text-lg font-bold text-white mb-3">Borderò archiviati</h3>
      {loading ? (
        <div className="card-dark p-10 text-center text-gray-400 font-manrope">Caricamento…</div>
      ) : sessions.length === 0 ? (
        <div className="card-dark p-10 text-center" data-testid="siae-empty-state">
          <FileText size={40} className="mx-auto text-gold opacity-40 mb-3" />
          <p className="text-white font-playfair text-lg mb-1">Nessun borderò creato</p>
          <p className="text-gray-400 font-manrope text-sm">Clicca &laquo;Nuovo borderò&raquo; per iniziare.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div key={s.id} data-testid={`siae-session-row-${s.id}`} className="card-dark p-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-playfair font-bold text-white text-base leading-tight truncate">
                    {s.title || s.event_title || 'Borderò senza titolo'}
                  </p>
                  <span className="text-xs font-manrope font-700 px-2 py-0.5 rounded-full" style={s.venue_type === 'jimmys' ? {
                    background: 'rgba(236,72,153,0.15)', color: '#ec4899',
                  } : {
                    background: 'rgba(201,168,76,0.1)', color: '#c9a84c',
                  }}>
                    {VENUES.find(v => v.value === s.venue_type)?.label || s.venue_type}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs font-manrope text-gray-400 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar size={12} /> {s.performance_date}</span>
                  {s.performer_name && <span>· {s.performer_name}</span>}
                  <span>· {s.songs?.length || 0} brani</span>
                  <span>· {SESSION_TYPES.find(t => t.value === s.session_type)?.label}</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => exportOne(s)} data-testid={`siae-export-${s.id}`} className="btn-secondary text-xs" title="Esporta PDF">
                  <Download size={14} /> PDF
                </button>
                <button onClick={() => setEditing(s)} data-testid={`siae-edit-${s.id}`} className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-400 hover:border-gold hover:text-gold transition-all" title="Modifica">
                  <Edit3 size={15} />
                </button>
                <button onClick={() => remove(s.id)} data-testid={`siae-delete-${s.id}`} className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-400 hover:border-red-400 hover:text-red-400 transition-all" title="Elimina">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {editing && (
        <SessionEditorModal
          token={token}
          session={editing.id ? editing : null}
          settings={settings}
          allEvents={allEvents}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
